import AgentBreadcrumb from '@/components/Agents/AgentBreadcrumb/AgentBreadcrumb';
import { agentDisplayMap, isAgentEnabled, parseAgentUrlSlug } from '@/components/Agents/AgentUtils';
import { _isNil, _notNil } from '@/littledash';
import type { AgentSessionApiId, AgentTreeNodeApiId } from '@/model/Agent.model';
import { featuresSelector } from '@/support/Selectors';
import { web as webRoute } from '@/support/route';
import { useModalAction } from '@/utils/modal';
import { type FC, useCallback, useMemo, useRef } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AgentSessionView from './AgentSessionView';
import type { AgentNodeHandlers } from './AgentTree.utils';

/**
 * Page for an agent session: breadcrumb header above a full-bleed React Flow canvas. The
 * Configuration (left) and Activity (right) panels are rendered as React Flow Panel overlays inside
 * the canvas — see `AgentSessionTree`.
 *
 * Reached at `/ai-agents/:slug/sessions/:sessionApiId`.
 */
const AgentSessionPage: FC = () => {
  const { slug, sessionApiId } = useParams<{ slug: string; sessionApiId: AgentSessionApiId }>();
  const features = useSelector(featuresSelector);
  const agentType = _notNil(slug) ? parseAgentUrlSlug(slug) : undefined;
  const agentName = _notNil(agentType) ? agentDisplayMap[agentType].name : 'Session';
  const { openModal } = useModalAction();

  // Published by the always-mounted session view (below) with its `/tree` reconcile. Confirming SEND
  // domains queues the validate execution server-side (flipping the session back to `running`), so we
  // reconcile on confirm to re-spin the root badge immediately rather than await the next stream tick.
  const reconcileRef = useRef<(() => Promise<void>) | null>(null);

  const onViewReport = useCallback(
    (nodeApiId: AgentTreeNodeApiId) => openModal('AGENT_REPORT', { sessionApiId, nodeApiId }),
    [openModal, sessionApiId]
  );

  // The typed SEND result lives on the session's validation execution, not the node, so the SEND
  // results modal takes only the session id (the modal fetches the session and reads the result).
  const onSendViewResults = useCallback(
    () => openModal('AGENT_SEND_RESULTS', { sessionApiId }),
    [openModal, sessionApiId]
  );

  // The per-file detections live on the session's detect execution, so the confirm modal likewise
  // takes only the session id (it fetches the session and reads the detected bundle). `onConfirmed`
  // fires once the confirm has queued the validate execution → reconcile to re-spin the root badge.
  const onSendConfirmDomains = useCallback(
    () => openModal('AGENT_SEND_CONFIRM', { sessionApiId, onConfirmed: () => void reconcileRef.current?.() }),
    [openModal, sessionApiId]
  );

  const handlers = useMemo<AgentNodeHandlers>(
    () => ({ onViewReport, onSendViewResults, onSendConfirmDomains }),
    [onViewReport, onSendViewResults, onSendConfirmDomains]
  );

  if (_notNil(agentType) && !isAgentEnabled(features, agentType)) {
    return <Navigate to={webRoute('agents')} replace />;
  }

  if (_isNil(sessionApiId)) {
    return (
      <div className="ph4 pb4 pt3" data-test-component="AgentSessionPage">
        <p className="mid-gray">Agent session not found.</p>
      </div>
    );
  }

  // `minHeight: 0` lets this flex-column shrink below content size so the canvas fills instead of
  // overflowing the viewport; there is no `min-h-0` Tachyons utility.
  return (
    <div
      className="flex flex-column h-100 bg-light-gray"
      style={{ minHeight: 0 }}
      data-test-component="AgentSessionPage"
    >
      <div className="ph4 pt3">
        <AgentBreadcrumb current={agentName} />
      </div>
      {/* key forces a remount (fresh fetch + engine + stream) when the route switches sessions. */}
      <AgentSessionView
        key={sessionApiId}
        sessionApiId={sessionApiId}
        handlers={handlers}
        reconcileRef={reconcileRef}
      />
    </div>
  );
};

export default AgentSessionPage;
